import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ScreenState } from '../screen-state';
import { useLiveInvalidation } from '../../live/use-live';
import { LEAVE_TOPICS } from '../../live/topics';
import { useGoUp, useWorkspaceNavigate } from '../../workspace-path';
import { RequestSurface, type RequestMode } from './request-surface';
import { useRequestDetail } from './use-request-detail';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `/requests/:id` — one request as a PAGE. P3-9's third surface.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The address is the page; the drawer and the modal are overlays over a list.** So this route
 * opens `full`, and shrinking to a drawer is a mode this component holds rather than a navigation —
 * `task-route.tsx` records what happens when a mode switch navigates instead: the list reloads
 * under a drawer that was supposed to be the same request.
 *
 * ⚠️ **It serves both kinds.** The id carries no discriminator, so `useRequestDetail` asks the
 * leave endpoint and falls back to the overtime one — which is what finally gives an overtime claim
 * the detail screen it never had.
 */
export function RequestRoute() {
  const go = useWorkspaceNavigate();
  const goUp = useGoUp();
  const { id = '' } = useParams();
  const [mode, setMode] = useState<RequestMode>('full');

  const { data, failure, serverError, reload, handlers } = useRequestDetail(id);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(LEAVE_TOPICS, reload);

  /* ⚠️ R-57 · the address names a request by its public identifier. One opened from a uuid — an
     older link, an email — swaps its address for the identifier once it has loaded, replacing the
     history entry rather than adding one, so Back does not land on the address it just left. */
  const identifier =
    data === null ? null : data.kind === 'leave' ? data.request.identifier : data.claim.identifier;
  useEffect(() => {
    if (identifier !== null && identifier !== id) go(`/requests/${identifier}`, { replace: true });
  }, [identifier, id, go]);

  return (
    <ScreenState
      failure={failure === null ? null : { kind: 'error', code: failure }}
      onRetry={() => void reload()}
    >
      {data === null ? null : (
        <RequestSurface
          mode={mode}
          open
          data={data}
          serverError={serverError}
          handlers={{
            ...handlers,
            onOpenTask: (key) => {
              go(`/tasks/${key}`);
            },
          }}
          onModeChange={setMode}
          /* ⚠️ The breadcrumb's segments. Only the full page has somewhere to send somebody —
             an overlay over a list closes back onto it. */
          onNavigate={go}
          /* ⚠️ Up to the list, wherever the request was opened from (R-61, 3 Nov 2026). */
          onClose={() => {
            goUp('/requests');
          }}
        />
      )}
    </ScreenState>
  );
}
